/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import React, {
  ChangeEvent,
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import {ColDef} from 'ag-grid-community';
import isNull from 'lodash.isnull';
import EntityLabel from '@ion/core/src/entities/EntityLabel';
import {isRestricted} from '@ion/core/src/entities/LabelRulesValidator';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {loadDevicesBySmartSearch} from '@ion/core/src/services/Connect';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {Device} from '@ion/core/src/types/entities';
import {formatDateTime} from '@ion/core/src/utils/DateUtil';
import {getDeviceFilterValue} from '@ion/core/src/utils/DeviceUtils';
import {clone} from '@ion/core/src/utils/ObjectUtil';
import {isEmpty} from '@ion/core/src/utils/StringUtil';
import {SearchField} from '@ion/dls/src/components/search/SearchField';
import {defaultTextFilterParams} from '@ion/dls/src/grid/ColumnDefinitions';
import {resolveDefinitions} from '@ion/dls/src/grid/ColumnResolver';
import {
  IonGrid,
  IonGridAPI
} from '@ion/dls/src/grid/IonGrid';
import {SortModelType} from '@ion/dls/src/grid/IonGridUtils';
import {GridResult} from '@ion/dls/src/grid/PaginationControls';
import {PAGE_SIZES} from '@ion/dls/src/grid/settings';
import {IonSnackbar} from '@ion/dls/src/notifications/IonSnackbar';
import {translateStatus} from '@ion/dls/src/utils/ComponentUtils';
import Dropdown from '../../components/menus/Dropdown';
import {
  getGridCellStyles,
  getGridDragStyles,
  getGridHeaderStyles,
  getGridSortStyles
} from '../../grid/IonGridUtils';
import DragIcon from '../../icons/current/DragIcon';
import HeaderHoverSortIcon from '../../icons/current/HeaderHoverSortIcon';
import HeaderSortDownIcon from '../../icons/current/HeaderSortDownIcon';
import HeaderSortIcon from '../../icons/current/HeaderSortIcon';
import HeaderSortUpIcon from '../../icons/current/HeaderSortUpIcon';
import HoverDragIcon from '../../icons/current/HoverDragIcon';
import {toggleThemeStyle} from '../../theme/helpers';
import {dlsColor} from '../../theme/theme';
import useThemeContext from '../../theme/useThemeContext';
import {DLSTheme, ViewSubGroupsProps} from '../../types/types';
import {getUniqItems} from '../ListEditorUtils';

const spaceRegex = /,|\s|\s,|,\s/g;

const useStyles = makeStyles((theme: DLSTheme) => ({
  search : {
    width                          : '536px',
    alignItems                     : 'center',
    justifyContent                 : 'center',
    gap                            : '4px',
    display                        : 'flex',
    flexDirection                  : 'row',
    marginBottom                   : '16px',
    marginLeft                     : toggleThemeStyle('0', '16px'),
    [theme.breakpoints.down('sm')] : {
      width         : '100%',
      flexDirection : 'column',
      alignItems    : 'start',
      marginLeft    : '0px !important'
    }
  },
  searchType : {
    width : '264px'
  },
  searchTypeInput : {
    padding  : '5px 2em !important',
    fontSize : '14px'
  },
  searchTypeOption : {
    fontSize : '14px !important'
  },
  searchInput : {
    fontSize : '14px !important'
  },
  endAdornment : {
    display              : 'flex',
    padding              : '0 10px',
    cursor               : 'pointer',
    '& .MuiSvgIcon-root' : {
      fontSize : 16
    }
  },
  deviceGridRoot : {
    ...getGridSortStyles({
      headerSortIcon      : <HeaderSortIcon />,
      headerSortUpIcon    : <HeaderSortUpIcon />,
      headerSortDownIcon  : <HeaderSortDownIcon />,
      headerHoverSortIcon : <HeaderHoverSortIcon />
    }),
    ...getGridDragStyles({
      dragIcon      : <DragIcon />,
      hoverDragIcon : <HoverDragIcon />
    }),
    ...getGridHeaderStyles(),
    ...getGridCellStyles()
  },
  currentDeviceGridContainer : {
    height                         : 'calc(100vh - 456px) !important',
    marginLeft                     : '20px',
    width                          : '95%',
    [theme.breakpoints.down('sm')] : {
      marginLeft : toggleThemeStyle('20px', '0px'),
      width      : toggleThemeStyle('95%', '100%'),
      height     : toggleThemeStyle('calc(100vh - 456px) !important', 'calc(100vh - 476px) !important')
    }
  },
  deviceGridContainer : {
    height                         : 'calc(100vh - 472px) !important',
    [theme.breakpoints.down('sm')] : {
      height : 'calc(100vh - 518px) !important'
    }
  }
}));

const currentRole = EntityLabel.getRole();

export interface DeviceDetailViewGridProps extends ViewSubGroupsProps {
  accountId: any
  setDeviceDetailedRow?: Function;
  singleRowSelection?: boolean;
  isCtcAdmin?: boolean;
  isGreyOutRow?: Function;
  deviceDetailedSelectedRow?: any;
  usePreferedPageSize?: boolean;
  additionalSearchQuery?: string;
  includeCheckboxes?: boolean;
  manageSelect?: boolean;
  initializeCheckboxes?: boolean;
  hideSubGroups?: boolean;
};

const defaultSort: SortModelType = {
  colId : 'name',
  sort  : 'asc'
};

enum SearchType {
  Any = 'any',
  ESN = 'esn',
  Name = 'name',
  IMEI = 'imei',
  SerialNumber = 'serialNumber'
};

const getSearchTypeOptions = () => ([{
  label : translate('admin-devices:modularsearch.labels.any'),
  value : SearchType.Any
}, {
  label : translate('admin-devices:modularsearch.labels.name'),
  value : SearchType.Name
}, {
  label : translate('admin-devices:modularsearch.labels.esn'),
  value : SearchType.ESN
}, {
  label : translate('admin-devices:modularsearch.labels.serialNumber'),
  value : SearchType.SerialNumber
}, {
  label : translate('admin-devices:modularsearch.labels.imei'),
  value : SearchType.IMEI
}]);

/**
 * Grid to be rendered on the Detailed Device popup
 */
export const DeviceDetailViewGrid = ({
  accountId,
  isGreyOutRow,
  toggleViewSubGroups,
  setDeviceDetailedRow,
  deviceDetailedSelectedRow,
  isCtcAdmin = false,
  manageSelect = false,
  viewSubGroups = false,
  hideSubGroups = false,
  singleRowSelection = true,
  includeCheckboxes = false,
  additionalSearchQuery = '',
  usePreferedPageSize = false,
  initializeCheckboxes = false
}: DeviceDetailViewGridProps, ref) => {
  const {isCurrent : isCurrentTheme}              = useThemeContext();
  const classes                                   = useStyles();
  const ionGridRef                                = useRef<IonGridAPI>();
  const [gridRef, setGridRef]                     = useState({});
  const [gridItems, setGridItems]                 = useState([]);
  const [gridApi, setGridApi]                     = useState(null);
  const [refreshRequired, setRefreshRequired]     = useState(false);
  const [searchQueryString, setSearchQueryString] = useState('id:*');
  const [searchValue, setSearchValue]             = useState('');
  const [showSnackBar, setShowSnackBar]           = useState(false);
  const [searchType, setSearchType]               = useState(SearchType.Any);

  const searchFieldStyles = useCallback(() => ({
    searchFieldRoot : toggleThemeStyle({
      height : '55px'
    }, {
      gap             : '8px',
      border          : `1px solid ${dlsColor('grey5')}`,
      height          : '50px',
      display         : 'flex',
      padding         : '8px',
      borderRadius    : '8px',
      backgroundColor : dlsColor('white')
    }),
    searchFieldInput : {
      fontSize : '16px'
    }
  }), []);

  useEffect(() => {
    if ((includeCheckboxes && initializeCheckboxes) || manageSelect) {
      gridApi?.deselectAll();
      checkBoxes(deviceDetailedSelectedRow);
    }
    gridApi?.hideOverlay();
    if (gridApi && Array.isArray(gridItems) && !gridItems.length) {
      gridApi.showNoRowsOverlay();
    }
  }, [gridItems, deviceDetailedSelectedRow]);

  const checkBoxes = useCallback(selectedItems => {
    const idMap = selectedItems?.reduce?.((acc, {id}) => {
      acc[id] = true;
      return acc;
    }, {});
    idMap && gridApi?.forEachNode((node) => {
      node.setSelected(!!idMap[node.data?.id]);
    });
  }, [gridApi]);

  useEffect(() => {
    searchQueryString !== 'id:*' && setRefreshRequired(true);
  }, [searchQueryString]);

  const getFilter = useCallback((filter) => {
    let filterStr = '(id:*)';
    if (!isNull(filter)) {
      const filterEntries = Object.entries(filter);
      for (const [key, value] of filterEntries) {
        const filterValue = getDeviceFilterValue(value as string);
        filterStr += ` AND ${key}: ${filterValue === value ? value : filterValue}`;
      }
    }
    return filterStr;
  }, []);

  const isSearchValueString = useCallback((value) => {
    const searchTermNames = value.replace(spaceRegex, ' ').replace(/ +/g, ' ').split(' ');
    return searchTermNames.some(item => isNaN(item));
  }, []);

  const createSearch = useCallback((value: string): string => {
    let queryString = 'id:*';
    const searchString = value.trim().replace(spaceRegex, ' ');
    let updated = searchString.includes(':') ? searchString : searchString.replace(/ +/g, ' ');
    if (updated[0] === ',') {
      updated = updated.substring(1);
    }
    if (updated[updated.length - 1] === ',') {
      updated = updated.substring(0, updated.length - 1);
    }
    if (updated.includes(':') || updated.includes('OR') || updated.includes('AND')) {
      queryString = updated;
    } else if (searchString) {
      if (searchType !== SearchType.Any) {
        queryString = `${searchType}: *${updated}*`;
      } else {
        queryString = `name: *${updated}* OR esn: *${updated}* OR serialNumber: *${updated}* OR imei: *${updated}*`;
      }
    } else if (!isEmpty(updated)) {
      queryString = `*${updated}*`;
    }
    return queryString;
  }, [isSearchValueString, searchType]);

  /**
   * Returns a reference to the grids public api ..
   */

  const getDevices = useCallback(async function({
    page,
    pageSize,
    sort,
    filter,
    callback
  }) {
    let filterStr = '';
    filterStr = `(${getFilter(filter)} AND (${createSearch(searchValue)})${additionalSearchQuery})`;
    try {
      const {devices: items, totalCount} = await loadDevicesBySmartSearch({
        accountId,
        pageId           : page,
        pageSize         : pageSize,
        sortOn           : sort,
        queryString      : filterStr,
        includeSubGroups : viewSubGroups as boolean
      });
      const devices = clone(items);
      const results = {items: devices, totalCount: totalCount};
      callback(results);
      setGridItems(devices);
      return {devices, totalCount};
    } catch (err) {
      callback({items: [], totalCount: 0});
      setGridItems([]);
      setShowSnackBar(true);
      error('Error occurred retrieving detailed device data.');
    }
  }, [
    getFilter,
    accountId,
    createSearch,
    searchValue,
    viewSubGroups,
    additionalSearchQuery
  ]);

  // Provides reference to the grid's api.
  const onGridReady = useCallback(api => {
    setGridApi(api);
  }, []);

  const onGridAPI = useCallback(api => {
    setGridRef(api);
  }, []);

  const onSearchChange = useCallback((e: ChangeEvent<{name?: string; value: string;}>) => {
    e.persist();
    setSearchValue(e.target.value);
    if (e.target.value === '') {
      setSearchQueryString('id:*');
      setRefreshRequired(true);
    }
  }, []);

  const handleDeviceSearch = useCallback(() => {
    setSearchQueryString(createSearch(searchValue));
  }, [createSearch, searchValue]);

  const removeSerach = useCallback(() => {
    setSearchValue('');
    setSearchQueryString('id:*');
    setRefreshRequired(true);
  }, []);

  const handleRowClicked = useCallback((event) => {
    const rowsSelected = event?.data;
    setDeviceDetailedRow?.(rowsSelected);
  }, [setDeviceDetailedRow]);

  const onSelectionChanged = useCallback((event) => {
    const rowsSelected = event.api.getSelectedRows();
    const selecteDevices  = [...rowsSelected, ...deviceDetailedSelectedRow];
    const uniqItems       = getUniqItems(selecteDevices);
    setDeviceDetailedRow?.(uniqItems);
  }, [deviceDetailedSelectedRow, setDeviceDetailedRow]);

  const closeSnackbar = useCallback(() => {
    setShowSnackBar(false);
  }, []);

  const handleSearchTypeChange = useCallback((e) => {
    setSearchType(e.target.value);
    if (searchValue) {
      setRefreshRequired(true);
    }
  }, [searchValue]);

  const searchTypeLabel = useMemo(() => (
    getSearchTypeOptions().find((type) => type.value === searchType)?.label
  ), [searchType]);

  const getGridHeight = useCallback(() => {
    return isCurrentTheme ? classes.deviceGridRoot : '';
  }, [classes.deviceGridRoot, isCurrentTheme]);

  return (
    <>
      <div className={classes.search}>
        <div className={classes.searchType}>
          <Dropdown options={getSearchTypeOptions()}
            value={searchType}
            externalClasses={{
              input  : classes.searchTypeInput,
              option : classes.searchTypeOption
            }}
            labelAsValue={false}
            onChange={handleSearchTypeChange} />
        </div>
        <SearchField placeholder={translate('admin-assets:form.sections.searchPlaceholder', {
          type : searchTypeLabel
        })}
        overrides={searchFieldStyles}
        onChange={onSearchChange}
        externalClasses={{
          input : classes.searchInput
        }}
        optionsProps={{
          detailedDeviceGridSearch : true,
          onKeyPress               : handleDeviceSearch,
          value                    : searchValue
        }}
        endAdornment={searchValue
          ? (
            <div className={classes.endAdornment}
              onClick={removeSerach}>
              <CloseIcon />
            </div>
          ) : null} />
      </div>
      <div className={isCurrentTheme ? classes.currentDeviceGridContainer : classes.deviceGridContainer}>
        <IonGrid ref={ionGridRef}
          omitHeader
          paginationEnabled
          showCustomPaginationBar
          getGridHeight={getGridHeight}
          showHeaderCheckbox={includeCheckboxes}
          checkboxesEnabled={includeCheckboxes}
          refreshRequired={refreshRequired}
          setRefreshRequired={setRefreshRequired}
          loadItems={getDevices}
          augmentItems={constructDeviceDetailedGridRows}
          attributeMappings={attributeMappings}
          columnDefinitions={getColumnDefinitions(isCtcAdmin)}
          pageOffset
          pageSizes={PAGE_SIZES}
          rowMenusEnabled={false}
          defaultSort={defaultSort}
          singleRowSelection={singleRowSelection}
          rowClassRules={{
            'ag-grid-row-grey-out' : isGreyOutRow
          }}
          toggleViewSubGroups={toggleViewSubGroups}
          viewSubGroups={viewSubGroups as boolean}
          onRowClicked={handleRowClicked}
          onGridAPI={onGridAPI}
          onGridReady={onGridReady}
          gridOptions={isCtcAdmin && {onSelectionChanged}}
          hideSubGroups={hideSubGroups}
          usePreferedPageSize={usePreferedPageSize} />
      </div>
      <IonSnackbar show={showSnackBar}
        message={translate('admin-assets:form.sections.deviceDetailedError')}
        actionLabel={translate('common:dismiss')}
        onAction={closeSnackbar}
        onClose={closeSnackbar}
        autoHideDuration={5000} />
    </>
  );
};

/**
 * Get any device identifier and return as device name
 * when the device name is empty.
 */
export const getDeviceIdentifier = (device: Device) => {
  return device?.esn ||
  device?.serialNumber ||
  device?.iccid ||
  device?.meid ||
  device?.imei ||
  device?.imsi ||
  device?.mdn ||
  device?.externalId ||
  device?.ipAddress;
};

export const getDeviceIdentifierData = (device: Device) => {
  const deviceIdentifier = translate('admin-devices:choosers.deviceIdentifierTypes.options', {returnObjects: true}).find(option => device[option.value]);
  return {label: deviceIdentifier?.label, value: device[deviceIdentifier?.value]};
};

/**
 * @constructDeviceDetailedGridRows
 *
 * Converts a collection of devices and converts them to a list of items specific
 * for display within the Admin Devices Grid, while retaining all of the items existing
 * attributes,
 */
export const constructDeviceDetailedGridRows = (devices: Device[]): GridResult[] => {
  attributeMappings.deviceType = devices;
  return devices.map(device => ({
    ...device,
    name         : device?.name || getDeviceIdentifier(device),
    deviceType   : device?.type?.title,
    group        : isRestricted(currentRole) ? entityId(device?.account?.href) : device?.account?.title,
    imei         : device?.imei,
    assetName    : device?.asset?.title,
    lastReported : formatDateTime(device?.lastAvlCommunication?.locationTime),
    status       : translateStatus(device?.status)
  })) as GridResult[];
};

const attributeMappings = {
  deviceType : {}
};

/**
 * @getColumnDef
 *
 * Returns column definitions for admin devices grid
 */
const getColumnDef = (isCtcAdmin: boolean, isMobile = false): ColDef[] => ([{
  headerName   : translate('admin-devices:grid.columns.deviceName'),
  field        : 'name',
  sortable     : true,
  filter       : false,
  resizable    : true,
  unSortIcon   : true,
  hide         : false,
  filterParams : defaultTextFilterParams
}, {
  headerName : translate('admin-devices:grid.columns.deviceType'),
  field      : 'deviceType',
  sortable   : false,
  filter     : false,
  resizable  : true,
  unSortIcon : false,
  hide       : false
}, {
  headerName : translate('admin-devices:grid.columns.account'),
  field      : 'group',
  sortable   : false,
  filter     : false,
  resizable  : true,
  unSortIcon : false,
  hide       : false
}, {
  headerName   : translate('admin-devices:grid.columns.esn'),
  field        : 'esn',
  sortable     : true,
  filter       : false,
  resizable    : true,
  unSortIcon   : true,
  hide         : false,
  filterParams : defaultTextFilterParams
}, {
  headerName   : translate('admin-devices:grid.columns.serialNumber'),
  field        : 'serialNumber',
  sortable     : true,
  filter       : false,
  resizable    : true,
  unSortIcon   : true,
  hide         : false,
  filterParams : defaultTextFilterParams
}, {
  headerName   : translate('admin-devices:grid.columns.imei'),
  field        : 'imei',
  sortable     : true,
  filter       : false,
  resizable    : true,
  unSortIcon   : true,
  hide         : false,
  filterParams : defaultTextFilterParams
}, {
  headerName : translate(`admin-devices:grid.columns.${isCtcAdmin ? 'assetName' : 'asset'}`),
  field      : 'assetName',
  sortable   : false,
  filter     : false,
  resizable  : true,
  unSortIcon : false,
  hide       : false
}, {
  headerName : translate('admin-devices:grid.columns.lastEventTime'),
  field      : 'lastReported',
  sortable   : false,
  filter     : false,
  resizable  : true,
  unSortIcon : false,
  hide       : false
}]);

const getColumnDefinitions = (isCtcAdmin: boolean) => ({
  desktop : resolveDefinitions(getColumnDef(isCtcAdmin)),
  mobile  : resolveDefinitions(getColumnDef(isCtcAdmin, true))
});

export default memo(forwardRef(DeviceDetailViewGrid));
